import { useState, useEffect } from "react";
import { RefreshCw, Utensils, Sun, Moon, Users } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BuoniPastoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/sendme/kitchen`);
      if (!response.ok) throw new Error("Errore nel caricamento");
      const result = await response.json();
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-2xl text-white">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-3xl text-red-500 mb-4">Errore: {error}</p>
          <button
            onClick={fetchData}
            className="px-8 py-4 bg-amber-500 text-black text-2xl font-bold rounded-xl hover:bg-amber-400 transition"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Utensils className="w-12 h-12 md:w-16 md:h-16 text-amber-500" />
          <div>
            <h1 className="text-3xl md:text-5xl font-bold text-amber-500">
              Cucina - Pasti da Preparare
            </h1>
            <p className="text-lg md:text-xl text-slate-400">
              SendMe Camp 2025 - Aggiornamento automatico
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-lg transition disabled:opacity-50"
        >
          <RefreshCw className={`w-6 h-6 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {/* Main Grid */}
      <div className="grid gap-4 md:gap-6">
        {data?.days?.map((day) => (
          <div
            key={day.day_key}
            className="bg-slate-800 rounded-2xl p-4 md:p-6 border border-slate-700"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Day Label */}
              <div className="flex-shrink-0">
                <h2 className="text-2xl md:text-4xl font-bold text-white">
                  {day.label}
                </h2>
              </div>

              {/* Meals */}
              <div className="flex flex-wrap gap-4 md:gap-8 items-center">
                {/* Pranzo */}
                {day.pranzo !== null ? (
                  <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-6 py-4">
                    <Sun className="w-10 h-10 md:w-12 md:h-12 text-amber-500" />
                    <div>
                      <p className="text-sm md:text-base text-amber-500 font-medium uppercase tracking-wide">
                        Pranzo
                      </p>
                      <p className="text-4xl md:text-6xl font-bold text-amber-500">
                        {day.pranzo}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-slate-700/50 border border-slate-600 rounded-xl px-6 py-4 opacity-40">
                    <Sun className="w-10 h-10 md:w-12 md:h-12 text-slate-500" />
                    <div>
                      <p className="text-sm md:text-base text-slate-500 font-medium uppercase tracking-wide">
                        Pranzo
                      </p>
                      <p className="text-4xl md:text-6xl font-bold text-slate-500">
                        -
                      </p>
                    </div>
                  </div>
                )}

                {/* Cena */}
                {day.cena !== null ? (
                  <div className="flex items-center gap-3 bg-indigo-500/10 border border-indigo-500/30 rounded-xl px-6 py-4">
                    <Moon className="w-10 h-10 md:w-12 md:h-12 text-indigo-400" />
                    <div>
                      <p className="text-sm md:text-base text-indigo-400 font-medium uppercase tracking-wide">
                        Cena
                      </p>
                      <p className="text-4xl md:text-6xl font-bold text-indigo-400">
                        {day.cena}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-slate-700/50 border border-slate-600 rounded-xl px-6 py-4 opacity-40">
                    <Moon className="w-10 h-10 md:w-12 md:h-12 text-slate-500" />
                    <div>
                      <p className="text-sm md:text-base text-slate-500 font-medium uppercase tracking-wide">
                        Cena
                      </p>
                      <p className="text-4xl md:text-6xl font-bold text-slate-500">
                        -
                      </p>
                    </div>
                  </div>
                )}

                {/* Daily Total */}
                <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-6 py-4 ml-auto">
                  <Users className="w-10 h-10 md:w-12 md:h-12 text-emerald-500" />
                  <div>
                    <p className="text-sm md:text-base text-emerald-500 font-medium uppercase tracking-wide">
                      Totale
                    </p>
                    <p className="text-4xl md:text-6xl font-bold text-emerald-500">
                      {day.totale}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Grand Totals */}
      {data?.totals && (
        <div className="mt-8 bg-gradient-to-r from-amber-500/20 via-indigo-500/20 to-emerald-500/20 rounded-2xl p-6 md:p-8 border border-slate-600">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
            Riepilogo Totale Evento
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center bg-amber-500/10 rounded-xl p-6 border border-amber-500/30">
              <Sun className="w-12 h-12 text-amber-500 mx-auto mb-2" />
              <p className="text-lg text-amber-500 uppercase tracking-wide mb-1">
                Tot. Pranzi
              </p>
              <p className="text-5xl md:text-7xl font-bold text-amber-500">
                {data.totals.pranzo}
              </p>
            </div>
            <div className="text-center bg-indigo-500/10 rounded-xl p-6 border border-indigo-500/30">
              <Moon className="w-12 h-12 text-indigo-400 mx-auto mb-2" />
              <p className="text-lg text-indigo-400 uppercase tracking-wide mb-1">
                Tot. Cene
              </p>
              <p className="text-5xl md:text-7xl font-bold text-indigo-400">
                {data.totals.cena}
              </p>
            </div>
            <div className="text-center bg-emerald-500/10 rounded-xl p-6 border border-emerald-500/30">
              <Users className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <p className="text-lg text-emerald-500 uppercase tracking-wide mb-1">
                Tot. Pasti
              </p>
              <p className="text-5xl md:text-7xl font-bold text-emerald-500">
                {data.totals.totale}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-slate-500">
        <p className="text-lg">
          Ultimo aggiornamento:{" "}
          {lastRefresh
            ? lastRefresh.toLocaleTimeString("it-IT", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
            : "-"}
        </p>
        <p className="text-sm mt-1">Aggiornamento automatico ogni 30 secondi</p>
      </div>
    </div>
  );
}
